import { GeistSans } from 'geist/font/sans';
import {
  Allura,
  Dancing_Script,
  Great_Vibes,
  Inter as InterFont,
  Noto_Sans,
  Noto_Serif,
  Pacifico,
  Satisfy,
  Vazirmatn as VazirmatnFont,
} from 'next/font/google';
import localFont from 'next/font/local';

// Keep a single source format per face where possible. `next/font/local`
// emits one font-face per `src` entry, so duplicate formats for the same
// weight/style can turn into duplicate preloads instead of true fallbacks.
/*
 * Exposure grade axis: `+` = lighter, `−` = heavier.
 * Files are served at the matching Tailwind/CSS font-weight values:
 *   [+20] Light    → 300
 *   [+10] Regular  → 400–450 (so the refresh's 450 doesn't snap to Medium)
 *   [0]   Medium   → 500
 *   [-10] SemiBold → 600
 *
 * Filenames leave the `+` off (`Exposure[20].woff2`): Turbopack keeps the
 * source basename in the emitted URL, and Vercel only serves a `+` there when
 * it is percent-encoded, so a raw `+` 404s the face.
 *
 * For usage guidance see packages/design-system/src/typography/typography.docs.md.
 */
const Exposure = localFont({
  src: [
    // Light [+20]
    {
      path: './fonts/Exposure/Exposure[20].woff2',
      weight: '300',
      style: 'normal',
    },
    {
      path: './fonts/Exposure/Exposure[20]-Italic.woff2',
      weight: '300',
      style: 'italic',
    },
    // Regular [+10]
    {
      path: './fonts/Exposure/Exposure[10].woff2',
      weight: '400 450',
      style: 'normal',
    },
    {
      path: './fonts/Exposure/Exposure[10]-Italic.woff2',
      weight: '400 450',
      style: 'italic',
    },
    // Medium [0]
    {
      path: './fonts/Exposure/Exposure[0].woff2',
      weight: '500',
      style: 'normal',
    },
    {
      path: './fonts/Exposure/Exposure[0]-Italic.woff2',
      weight: '500',
      style: 'italic',
    },
    // SemiBold [-10]
    {
      path: './fonts/Exposure/Exposure[-10].woff2',
      weight: '600',
      style: 'normal',
    },
    {
      path: './fonts/Exposure/Exposure[-10]-Italic.woff2',
      weight: '600',
      style: 'italic',
    },
  ],
  display: 'swap',
  preload: false,
  variable: '--font-exposure',
  // Match Exposure's serif metrics; the default is sans (Arial).
  adjustFontFallback: 'Times New Roman',
});

const Inter = InterFont({
  subsets: ['latin', 'latin-ext'],
  style: ['normal', 'italic'],
  display: 'swap',
  variable: '--font-inter',
});

// General Sans (Fontshare / Indian Type Foundry FF EULA — see
// ./fonts/GeneralSans/LICENSE.txt). Body face only behind
// `release-body-font-refresh`; `preload: false` keeps it off the critical path
// while the flag is off.
const generalSans = localFont({
  src: [
    {
      path: './fonts/GeneralSans/GeneralSans-Variable.woff2',
      weight: '200 700',
      style: 'normal',
    },
    {
      path: './fonts/GeneralSans/GeneralSans-VariableItalic.woff2',
      weight: '200 700',
      style: 'italic',
    },
  ],
  display: 'swap',
  preload: false,
  variable: '--font-general-sans',
});

const notoSans = Noto_Sans({
  subsets: ['latin', 'latin-ext', 'cyrillic', 'cyrillic-ext', 'greek', 'greek-ext'],
  display: 'swap',
  preload: false,
  variable: '--font-noto-sans',
});

const notoSerif = Noto_Serif({
  subsets: ['latin', 'latin-ext', 'cyrillic', 'cyrillic-ext', 'greek', 'greek-ext'],
  display: 'swap',
  preload: false,
  variable: '--font-noto-serif',
});

// Signature fonts
const dancingScript = Dancing_Script({
  subsets: ['latin'],
  display: 'swap',
  preload: false,
  variable: '--font-dancing-script',
});

const greatVibes = Great_Vibes({
  weight: '400',
  subsets: ['latin'],
  display: 'swap',
  preload: false,
  variable: '--font-great-vibes',
});

const allura = Allura({
  weight: '400',
  subsets: ['latin'],
  display: 'swap',
  preload: false,
  variable: '--font-allura',
});

const pacifico = Pacifico({
  weight: '400',
  subsets: ['latin'],
  display: 'swap',
  preload: false,
  variable: '--font-pacifico',
});

const satisfy = Satisfy({
  weight: '400',
  subsets: ['latin'],
  display: 'swap',
  preload: false,
  variable: '--font-satisfy',
});

// Arabic-script UI font. Inter/Exposure are Latin-only, so Arabic locales fall
// back to Vazirmatn via the `[lang='ar*']` rule in globals.css. Variable font,
// so no `weight` is specified.
const vazirmatn = VazirmatnFont({
  subsets: ['arabic', 'latin', 'latin-ext'],
  display: 'swap',
  preload: false,
  variable: '--font-vazirmatn',
});

/**
 * The standalone heidiTranslator's message font stack: Inter, then the OS
 * default for whatever Inter can't draw (Arabic script, CJK, Thai, Khmer,
 * Burmese, Indic, Greek — most of the translator's languages). Declared as
 * `--font-translator-transcript` in `_app.tsx` and pinned on translator message
 * text so it ignores `--font-body` / `--font-inter`, which the
 * `release-body-font-refresh` marker repoints to General Sans (Latin-only), and
 * the `[lang|='ar'] *` Vazirmatn rule. See ANZ-2440.
 */
const translatorTranscriptFontFamily = [
  Inter.style.fontFamily,
  '-apple-system',
  'BlinkMacSystemFont',
  "'Segoe UI'",
  'Roboto',
  'sans-serif',
].join(', ');

/**
 * Font CSS-var classes for the <main> font scope, grouped by role. Order is
 * cosmetic — each class sets its own distinct `--font-*` var, so none override
 * another; the grouping is for readability only.
 */
export const fontVariableClasses = [
  // Legacy core — still referenced, not yet retired.
  GeistSans.variable,
  // Current core — Exposure headings; Inter body (General Sans when
  // `release-body-font-refresh` is on).
  Exposure.variable,
  Inter.variable,
  generalSans.variable,
  // Locale fallbacks — non-Latin scripts (Noto CJK/Cyrillic/Greek, Vazirmatn Arabic).
  notoSans.variable,
  notoSerif.variable,
  vazirmatn.variable,
  // Signature faces — handwritten fonts for the Defaults footer signature editor.
  dancingScript.variable,
  greatVibes.variable,
  allura.variable,
  pacifico.variable,
  satisfy.variable,
].join(' ');

export {
  allura,
  dancingScript,
  Exposure,
  generalSans,
  greatVibes,
  Inter,
  notoSans,
  notoSerif,
  pacifico,
  satisfy,
  translatorTranscriptFontFamily,
  vazirmatn,
};
